import type { ConfigType } from '@plone/registry';
import cloneDeep from 'lodash/cloneDeep';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import imageSVG from '@plone/volto/icons/image.svg';
import rowSVG from '@plone/volto/icons/row.svg';
import quoteSVG from '@plone/volto/icons/quote.svg';
import emailSVG from '@plone/volto/icons/email.svg';
import alarmSVG from '@plone/volto/icons/alarm.svg';
import imagesSVG from '@plone/volto/icons/images.svg';
import imageFullSVG from '@plone/volto/icons/image-full.svg';
import sliderSVG from '@plone/volto/icons/slider.svg';
import appsSVG from '@plone/volto/icons/apps.svg';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import helpSVG from '@plone/volto/icons/help.svg';
import infoSVG from '@plone/volto/icons/info.svg';
import megaphoneSVG from '@plone/volto/icons/megaphone.svg';
import listBulletSVG from '@plone/volto/icons/list-bullet.svg';

import AnnouncementBarView from '../components/Blocks/AnnouncementBar/AnnouncementBarView';
import AnnouncementBarEdit from '../components/Blocks/AnnouncementBar/AnnouncementBarEdit';
import NextStepsView from '../components/Blocks/NextSteps/NextStepsView';
import NextStepsEdit from '../components/Blocks/NextSteps/NextStepsEdit';
import HeroBannerView from '../components/Blocks/HeroBanner/HeroBannerView';
import HeroBannerEdit from '../components/Blocks/HeroBanner/HeroBannerEdit';
import CalloutView from '../components/Blocks/Callout/CalloutView';
import CalloutEdit from '../components/Blocks/Callout/CalloutEdit';
import ContactCardView from '../components/Blocks/ContactCard/ContactCardView';
import ContactCardEdit from '../components/Blocks/ContactCard/ContactCardEdit';
import CountdownView from '../components/Blocks/Countdown/CountdownView';
import CountdownEdit from '../components/Blocks/Countdown/CountdownEdit';
import PhotoMosaicView from '../components/Blocks/PhotoMosaic/PhotoMosaicView';
import PhotoMosaicEdit from '../components/Blocks/PhotoMosaic/PhotoMosaicEdit';
import FeaturePanelView from '../components/Blocks/FeaturePanel/FeaturePanelView';
import FeaturePanelEdit from '../components/Blocks/FeaturePanel/FeaturePanelEdit';
import FeaturePanelSchema from '../components/Blocks/FeaturePanel/schema';
import MediaCarouselView from '../components/Blocks/MediaCarousel/MediaCarouselView';
import MediaCarouselEdit from '../components/Blocks/MediaCarousel/MediaCarouselEdit';
import NewsCarouselTemplate from '../components/Blocks/Listing/NewsCarouselTemplate';
import newsCarouselSchemaEnhancer from '../components/Blocks/Listing/newsCarouselSchema';
import NewsCenterTemplate from '../components/Blocks/Listing/NewsCenterTemplate';
import NewsPagination from '../components/NewsPagination/NewsPagination';
import FaqView from '../components/Blocks/Faq/FaqView';
import FaqEdit from '../components/Blocks/Faq/FaqEdit';
import InstitutionalBlurbView from '../components/Blocks/InstitutionalBlurb/InstitutionalBlurbView';
import InstitutionalBlurbEdit from '../components/Blocks/InstitutionalBlurb/InstitutionalBlurbEdit';
import InstitutionalBlurbSchema from '../components/Blocks/InstitutionalBlurb/schema';
import defaultBlurbItems from '../components/Blocks/InstitutionalBlurb/defaultItems';
import newsItemTemplate from './newsTemplate';
import IconCardsView from '../components/Blocks/IconCards/IconCardsView';
import IconCardsEdit from '../components/Blocks/IconCards/IconCardsEdit';
import SectionView from '../components/Blocks/Section/SectionView';
import SectionEdit from '../components/Blocks/Section/SectionEdit';
import SectionSchema from '../components/Blocks/Section/schema';
import {
  DateSocialShareView,
  DateSocialShareEdit,
} from '../components/Blocks/DateSocialShare';

// Grupo do BlockChooser para blocos usados só na home. O título é traduzido
// dinamicamente pelo core via `intl.formatMessage({ id: groupName.id, ... })`
// (ver BlockChooser.jsx) — este defineMessages só existe para o extrator de
// i18n encontrar a string (mesmo padrão do core em config/Blocks.jsx).
const messages = defineMessages({
  home: {
    id: 'home',
    defaultMessage: 'Home',
  },
});

/**
 * Registro dos blocos customizados do odc.
 * Cada bloco novo é adicionado aqui e importado no src/index.ts.
 */
export default function installBlocks(config: ConfigType) {
  // Novo grupo "Home" no block chooser, logo após "Most used" — reúne os
  // blocos usados só na home (heroBanner, section, countdown, photoMosaic,
  // iconCards, featurePanel). Preserva os grupos default do core.
  const mostUsedIndex = config.blocks.groupBlocksOrder.findIndex(
    (group: { id: string }) => group.id === 'mostUsed',
  );
  const homeGroup = { id: 'home', title: messages.home.defaultMessage };
  config.blocks.groupBlocksOrder = [
    ...config.blocks.groupBlocksOrder.slice(0, mostUsedIndex + 1),
    homeGroup,
    ...config.blocks.groupBlocksOrder.slice(mostUsedIndex + 1),
  ];

  config.blocks.blocksConfig.heroBanner = {
    id: 'heroBanner',
    title: 'Hero banner',
    icon: imageSVG,
    group: 'home',
    view: HeroBannerView,
    edit: HeroBannerEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // Faixa de novidade abaixo do header (1º bloco da home) — pedido da UNESCO
  // 2026-09, docs/specs/blocks/announcement-bar.md.
  config.blocks.blocksConfig.announcementBar = {
    id: 'announcementBar',
    title: 'Announcement bar',
    icon: megaphoneSVG,
    group: 'home',
    view: AnnouncementBarView,
    edit: AnnouncementBarEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // "Next Steps" — marcos até a Conferência, entre o countdown e o mosaico da
  // faixa About — pedido da UNESCO 2026-09, docs/specs/blocks/next-steps.md.
  config.blocks.blocksConfig.nextSteps = {
    id: 'nextSteps',
    title: 'Next steps',
    icon: listBulletSVG,
    group: 'home',
    view: NextStepsView,
    edit: NextStepsEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // Bloco FAQ — um bloco por tema na pagina /{lang}/faq
  // (docs/specs/blocks/faq.md).
  config.blocks.blocksConfig.faq = {
    id: 'faq',
    title: 'FAQ',
    icon: helpSVG,
    group: 'text',
    view: FaqView,
    edit: FaqEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // Faixa institucional do fim das notícias ("Sobre a Década do Oceano", "Sobre
  // a COI", "Sobre o MCTI") — docs/specs/blocks/institutional-blurb.md.
  //
  // Era um documento único (/{lang}/institucional) replicado em toda notícia
  // por um slot. Virou bloco em 2026-09-10: o cliente precisa do conteúdo
  // variando por notícia, conforme os parceiros envolvidos.
  config.blocks.blocksConfig.institutionalBlurb = {
    id: 'institutionalBlurb',
    title: 'Institutional blurb',
    icon: infoSVG,
    group: 'text',
    view: InstitutionalBlurbView,
    edit: InstitutionalBlurbEdit,
    blockSchema: InstitutionalBlurbSchema,
    // Conteúdo inicial de quem insere o bloco pela barra de blocos. Ao
    // contrário do `initialBlocks` (config estática, ver settings.ts), o
    // `initialValue` recebe o `intl` REAL — então os títulos saem no idioma da
    // notícia. É também por isso que o bloco é dedicado em vez de um `section`
    // com slates: `initialValue` é config POR BLOCO.
    initialValue: ({
      value,
      intl,
    }: {
      value: Record<string, unknown>;
      intl: IntlShape;
    }) => ({ ...value, items: defaultBlurbItems({ intl }) }),
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.callout = {
    id: 'callout',
    title: 'Callout',
    icon: quoteSVG,
    group: 'text',
    view: CalloutView,
    edit: CalloutEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.contactCard = {
    id: 'contactCard',
    title: 'Contact card',
    icon: emailSVG,
    group: 'common',
    view: ContactCardView,
    edit: ContactCardEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.countdown = {
    id: 'countdown',
    title: 'Countdown',
    icon: alarmSVG,
    group: 'home',
    view: CountdownView,
    edit: CountdownEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // Container (área de texto = blocos aninhados) — spec feature-panel.md §12.1
  config.blocks.blocksConfig.featurePanel = {
    id: 'featurePanel',
    title: 'Feature panel',
    icon: imageFullSVG,
    group: 'home',
    view: FeaturePanelView,
    edit: FeaturePanelEdit,
    blockSchema: FeaturePanelSchema,
    dataAdapter: ({
      block,
      data,
      id,
      onChangeBlock,
      value,
    }: {
      block: string;
      data: Record<string, unknown>;
      id: string;
      onChangeBlock: (block: string, data: Record<string, unknown>) => void;
      value: unknown;
    }) => {
      onChangeBlock(block, { ...data, [id]: value });
    },
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
    maxLength: 6,
    allowedBlocks: ['slate'],
  };

  config.blocks.blocksConfig.iconCards = {
    id: 'iconCards',
    title: 'Icon cards',
    icon: appsSVG,
    group: 'home',
    view: IconCardsView,
    edit: IconCardsEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.mediaCarousel = {
    id: 'mediaCarousel',
    title: 'Media carousel',
    icon: sliderSVG,
    group: 'media',
    view: MediaCarouselView,
    edit: MediaCarouselEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.dateSocialShareBlock = {
    id: 'dateSocialShareBlock',
    title: 'Date / Social share',
    icon: calendarSVG,
    group: 'layout',
    view: DateSocialShareView,
    edit: DateSocialShareEdit,
    restricted: ({ properties }: { properties: any; block: any }) =>
      properties.blocks_layout?.items?.find(
        (uid: string) =>
          properties.blocks?.[uid]?.['@type'] === 'dateSocialShareBlock',
      ),
    mostUsed: true,
    blockHasOwnFocusManagement: false,
    sidebarTab: 1,
  };

  // Variação "News carousel" do bloco Listing nativo (Latest News da home).
  // A query/limite/ordenação vêm do próprio Listing (querystring widget).
  config.blocks.blocksConfig.listing.variations = [
    ...(config.blocks.blocksConfig.listing.variations || []),
    {
      id: 'newsCarousel',
      title: 'News carousel',
      template: NewsCarouselTemplate,
      schemaEnhancer: newsCarouselSchemaEnhancer,
    },
    // Variação "News Center" — página /{lang}/noticias (docs/specs/blocks/
    // news-listing-page.md). paginationComponent é lido pelo ListingBody
    // sombreado (src/customizations/.../Listing/ListingBody.jsx).
    {
      id: 'newsCenter',
      title: 'News Center',
      template: NewsCenterTemplate,
      paginationComponent: NewsPagination,
    },
  ];

  config.blocks.blocksConfig.photoMosaic = {
    id: 'photoMosaic',
    title: 'Photo mosaic',
    icon: imagesSVG,
    group: 'home',
    view: PhotoMosaicView,
    edit: PhotoMosaicEdit,
    restricted: false,
    mostUsed: false,
    blockHasOwnFocusManagement: true,
    sidebarTab: 1,
  };

  // Section — container vertical com faixa de cor (About, Latest News).
  // Espelha o gridBlock do core: ContainerEdit + blockSchema + dataAdapter.
  // Ver docs/specs/blocks/section.md.
  config.blocks.blocksConfig.section = {
    id: 'section',
    title: 'Section',
    icon: rowSVG,
    group: 'home',
    view: SectionView,
    edit: SectionEdit,
    blockSchema: SectionSchema,
    dataAdapter: ({
      block,
      data,
      id,
      onChangeBlock,
      value,
    }: {
      block: string;
      data: Record<string, unknown>;
      id: string;
      onChangeBlock: (block: string, data: Record<string, unknown>) => void;
      value: unknown;
    }) => {
      onChangeBlock(block, { ...data, [id]: value });
    },
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
    maxLength: 12,
    // Whitelist deliberada — não liberar tudo (spec §3.1)
    allowedBlocks: [
      'slate',
      'countdown',
      'nextSteps',
      'photoMosaic',
      'listing',
    ],
  };
  // Template da notícia — ver newsTemplate.ts para o porquê de cada bloco.
  config.blocks.initialBlocks = {
    ...config.blocks.initialBlocks,
    'News Item': newsItemTemplate(config.settings?.defaultBlockType),
  };

  // O `getSizes` do core devolve `100vw` para a imagem grande centralizada, mas
  // neste tema ela para na coluna de leitura (740px, medido em
  // /noticias/<slug> de 768px para cima). Sem isto o navegador pede a escala do
  // tamanho da janela — 1600w (271 KB) para exibir 740px.
  const coreGetSizes = config.blocks.blocksConfig.image.getSizes;
  config.blocks.blocksConfig.image.getSizes = (data: {
    align?: string;
    size?: string;
  }) =>
    data.align === 'center' && data.size === 'l'
      ? '(max-width: 788px) 100vw, 740px'
      : coreGetSizes(data);

  // Config aninhada: os blocos internos enxergam o registro completo
  // (mesmo padrão do gridBlock do core). Clonar por último, com tudo registrado.
  config.blocks.blocksConfig.section.blocksConfig = cloneDeep(
    config.blocks.blocksConfig,
  );
  config.blocks.blocksConfig.featurePanel.blocksConfig = cloneDeep(
    config.blocks.blocksConfig,
  );

  return config;
}
